import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useToast } from '@/hooks/use-toast';
import { Search, Filter, Building, ArrowLeft, MapPin, Plus, Users, Star, Globe } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CompanySetupWizard } from '@/components/company/company-setup-wizard';

interface Company {
  id: string;
  name: string;
  industry: string;
  headquarters_location: string;
  logo_url: string | null;
  size_range: string;
  client?: {
    id: string;
    status: 'prospect' | 'client' | 'past_client';
    client_tier: 'startup' | 'growth' | 'enterprise' | null;
  };
}

export default function AdminCompaniesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [industryFilter, setIndustryFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (user) {
      fetchCompanies();
    }
  }, [user, clientFilter, industryFilter, page]);

  const fetchCompanies = async () => {
    try {
      let query = supabase
        .from('companies')
        .select(`
          *,
          client:clients(
            id,
            status,
            client_tier
          )
        `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      // Apply filters
      if (clientFilter === 'has_client') {
        query = query.not('client', 'is', null);
      } else if (clientFilter === 'no_client') {
        query = query.is('client', null);
      }

      if (industryFilter !== 'all') {
        query = query.eq('industry', industryFilter);
      }

      const { data, error, count } = await query;

      if (error) throw error;
      
      // Update hasMore based on count
      setHasMore(count ? (page + 1) * PAGE_SIZE < count : false);
      
      // Append or replace data based on page
      setCompanies(prev => page === 0 ? data || [] : [...prev, ...(data || [])]);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load companies'
      });
    } finally {
      setLoading(false);
    }
  };

  // Get unique industries for filtering
  const industries = [...new Set(companies.map(company => company.industry))].filter(Boolean);

  // Filter companies based on search query
  const filteredCompanies = companies.filter(company => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      company.name?.toLowerCase().includes(query) ||
      company.industry?.toLowerCase().includes(query) ||
      company.headquarters_location?.toLowerCase().includes(query)
    );
  });

  const getClientBadge = (company: Company) => {
    if (!company.client) {
      return <Badge variant="outline">No Client Record</Badge>;
    }

    switch (company.client.status) {
      case 'client':
        return (
          <div className="flex gap-2">
            <Badge className="bg-green-100 text-green-800">Active Client</Badge>
            {company.client.client_tier && (
              <Badge variant="outline">{company.client.client_tier}</Badge>
            )}
          </div>
        );
      case 'prospect':
        return <Badge className="bg-blue-100 text-blue-800">Prospect</Badge>;
      case 'past_client':
        return <Badge className="bg-gray-100 text-gray-800">Past Client</Badge>;
      default:
        return null;
    }
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  };

  if (loading && page === 0) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <div className="flex justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-8 px-4 md:px-6">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/admin">
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Companies</h1>
              <p className="text-muted-foreground">
                Manage all companies on the platform
              </p>
            </div>
          </div>
          <Button onClick={() => setFormOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Company
          </Button>
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search companies..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                <SelectItem value="has_client">Has Client Record</SelectItem>
                <SelectItem value="no_client">No Client Record</SelectItem>
              </SelectContent>
            </Select>

            <Select value={industryFilter} onValueChange={setIndustryFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Industries</SelectItem>
                {industries.map(industry => (
                  <SelectItem key={industry} value={industry}>{industry}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => {
                setSearchQuery('');
                setClientFilter('all');
                setIndustryFilter('all');
              }}
            >
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {filteredCompanies.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium">No Companies Found</h3>
                <p className="text-muted-foreground">
                  {searchQuery || clientFilter !== 'all' || industryFilter !== 'all' 
                    ? 'Try adjusting your search criteria' 
                    : 'No companies available'}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCompanies.map((company) => (
              <Card 
                key={company.id} 
                className="hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => navigate(`/admin/company/${company.id}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <Avatar className="h-12 w-12 border bg-white">
                      <AvatarImage 
                        src={company.logo_url || undefined} 
                        alt={company.name}
                        className="object-contain p-1"
                      />
                      <AvatarFallback className="text-lg">
                        {company.name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-base font-medium truncate flex-1">
                          {company.name}
                        </h3>
                      </div>
                      
                      <p className="text-sm text-muted-foreground mt-1 truncate">
                        {company.industry}
                      </p>
                      
                      {company.headquarters_location && (
                        <div className="flex items-center gap-1 mt-1 text-sm">
                          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="truncate">{company.headquarters_location}</span>
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 mt-2">
                        {getClientBadge(company)}
                        {company.size_range && (
                          <Badge variant="outline" className="gap-1">
                            <Users className="h-3 w-3" />
                            {company.size_range}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {hasMore && (
          <div className="flex justify-center mt-6">
            <Button 
              variant="outline" 
              onClick={loadMore}
              disabled={loading}
            >
              {loading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              ) : (
                'Load More'
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Add Company Dialog */}
      <CompanySetupWizard
        open={formOpen}
        onOpenChange={setFormOpen}
        onComplete={() => {
          setFormOpen(false);
          fetchCompanies();
        }}
      />
    </div>
  );
}